import { query } from 'api/prismic'
import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import { layoutQuery } from 'prismic/fragments/layoutQuery'
import { educationQuery } from 'prismic/queries/educationQuery'
import { PrismicRichText } from 'prismic/types/richtext'
import { Education, Layout } from 'prismic-types'

import { asText } from 'components/rich-text/RichText'
import { PrismicMeta } from 'containers/meta/PrismicMeta'
import { EducationContainer } from 'containers/page/EducationContainer'
import { EducationSlices } from 'containers/slices/EducationSlices'

import { localeToPrismicLocale } from 'utils/i18n'
import { pageTitleFromSlices } from 'utils/pageTitleFromSlices'
import {
  singleNavigationQuery,
  topNavigationQuery,
} from 'prismic/fragments/navigationQuery'

export type PageProps = {
  preview: boolean
  page: Education | null
  layout: Layout | null
}

export default function PageComponent(
  data: InferGetServerSidePropsType<typeof getServerSideProps>
) {
  const page = data.page ?? null

  if (!page) {
    return null
  }

  const pageTitle = asText(page.title as PrismicRichText)
  const title =
    pageTitle !== ''
      ? (page.title as PrismicRichText)
      : pageTitleFromSlices(page.slices ?? [])

  return (
    <>
      <PrismicMeta data={{ ...page, title }} layout={data.layout} />
      {pageTitle !== '' && <EducationContainer data={page} />}
      <EducationSlices
        slices={page.slices ?? []}
        date={page.published ?? page._meta.firstPublicationDate}
      />
    </>
  )
}

export const getServerSideProps: GetServerSideProps<PageProps> = async ({
  preview = false,
  previewData,
  params,
  resolvedUrl,
  locale,
}) => {
  const lang = localeToPrismicLocale(locale)
  const uid = params?.uid as string
  const variables = { uid, lang }
  const cacheKey = `${lang}-uid-${uid}`

  // GraphQL hack for deep navigation
  const topNavData = await query(topNavigationQuery, { variables: { lang } })
  const topNav =
    topNavData?.allNavigations.edges
      ?.filter((edge) => edge && edge.node)
      .map((edge) => {
        return edge?.node._meta.uid
      }) ?? []

  const megamenuItems = async () => {
    return await Promise.all(
      topNav.map((uid) =>
        query(singleNavigationQuery, { variables: { lang, uid: uid! } })
      )
    )
  }

  const [navData, layoutData, pageData] = await Promise.all([
    megamenuItems(),
    query(layoutQuery, {
      variables: { lang },
      cacheKey: `layout-${lang}`,
    }),
    query(educationQuery, {
      previewData,
      variables,
      cacheKey: `education-${cacheKey}`,
      breadcrumbs: [resolvedUrl],
    }),
  ])

  const layout: Layout | null = layoutData?.layout ?? null
  const page: Education | null = pageData?.education ?? null
  const navigation = navData.map((navDataItem) => navDataItem?.navigation)

  if (!page) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      preview,
      page,
      navigation,
      layout,
    },
  }
}
